import React, { useEffect, useMemo, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import styles from "./StockItemDetailsPage.module.css";

import Navbar from "../../components/Navbar";
import BottomNav from "../../components/BottomNav";
import Button from "Shared/Button";
import ItemDetails from "Shared/ItemDetails";
import ItemIconSection from "Shared/ItemIconSection";
import ItemImage from "Shared/ItemImage";
import ProductGallery from "../../components/ProductGallery";
import { useStockItem } from "../../hooks/useStockItems";
import { useNote, useUpdateNote } from "../../hooks/useItemInteractions";
import { useAddToCart, useCart, getCartItemCount } from "../../hooks/useCart";
import { trackProductViewed, trackProductAdded } from "../../utils/analytics";
import { getSizeAbbreviation, getSizeOrder } from "utils/sizeOrder";
import { formatReleaseDate } from "../../utils/formatReleaseDate";
import { computeStockStatus, lowStockMessage } from "../../utils/stockStatus";

// assets
import BackArrow from "../../assets/Back_Arrow.svg";
import EditIcon from "../../assets/EditListIcon.svg";
import ThirdPartyIcon from "assets/3rdPartyIcon.svg";
import HotBadge from "assets/HotBadge.svg";
import NewBadge from "assets/NewBadge.svg";

export default function StockItemDetailsPage({ pageFrom, location }) {
  const { stockId } = useParams();
  const history = useHistory();

  const { data: item, isLoading } = useStockItem(stockId);
  const { data: noteData } = useNote(stockId);
  const updateNote = useUpdateNote();
  const addToCart = useAddToCart();
  const { data: cartData } = useCart();
  const cartCount = getCartItemCount(cartData);

  // Get listId from location state for navigation back to specific list
  const listId = location?.state?.listId ?? pageFrom?.listId;

  // Determine back label and navigation based on pageFrom
  const isFromList =
    pageFrom &&
    (String(pageFrom).toLowerCase().includes("list") ||
      String(pageFrom).toLowerCase().includes("saved"));
  const backLabel = isFromList ? "WWG Lists" : "WWG Store";
  const backPath = isFromList
    ? listId
      ? `/my-lists/${listId}`
      : "/my-lists"
    : "/wwg-store";
  const backState = isFromList && listId ? { listId } : {};

  const [qty, setQty] = useState(1);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [noteText, setNoteText] = useState("");

  useEffect(() => {
    if (noteData?.text != null) setNoteText(noteData.text);
  }, [noteData?.text]);

  // Fires once per distinct product, not on every cache-hit re-render of
  // useStockItem (which multiple consumers can share).
  useEffect(() => {
    if (item?.stockId) trackProductViewed(item);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.stockId]);

  const colorOptions = useMemo(() => {
    const options = item?.detailColors || [];
    return options
      .map((option) => {
        if (typeof option === "string") {
          return { label: option, imageUrl: item?.imageUrl, isSoldOut: false };
        }
        return {
          label: option?.label || option?.value || "",
          imageUrl: option?.imageUrl || item?.imageUrl,
          images: Array.isArray(option?.images) ? option.images : [],
          isSoldOut: Boolean(option?.isSoldOut),
        };
      })
      .filter((option) => option.label);
  }, [item]);
  const [selectedColorIdx, setSelectedColorIdx] = useState(0);

  const sizeOptions = useMemo(() => {
    const options = item?.sizeOptions || [];
    return options
      .map((option) => {
        if (typeof option === "string") {
          return { label: option, isSoldOut: false };
        }
        return {
          label: option?.label || option?.value || "",
          isSoldOut: Boolean(option?.isSoldOut),
        };
      })
      .sort((a, b) => getSizeOrder(a.label) - getSizeOrder(b.label));
  }, [item]);

  const [selectedSize, setSelectedSize] = useState(
    sizeOptions.length ? sizeOptions[0].label : null
  );

  // On a fresh product load, default to an in-stock color+size combo rather
  // than always landing on index 0 (which may be sold out). Out-of-stock
  // options remain selectable afterward - see isSelectedVariantOutOfStock.
  useEffect(() => {
    if (!item) return;

    const defaultColorIdx = colorOptions.length
      ? (() => {
          const idx = colorOptions.findIndex((option) => !option.isSoldOut);
          return idx >= 0 ? idx : 0;
        })()
      : 0;
    setSelectedColorIdx(defaultColorIdx);

    if (!sizeOptions.length) {
      setSelectedSize(null);
      return;
    }

    const defaultColorLabel = colorOptions[defaultColorIdx]?.label;
    const sizesForDefaultColor =
      defaultColorLabel && item.variantMatrix
        ? sizeOptions.map((sizeOption) => {
            const key = `${defaultColorLabel}::${sizeOption.label}`;
            const variantEntry = item.variantMatrix[key];
            if (typeof variantEntry?.isAvailable !== "boolean") {
              return sizeOption;
            }
            return { ...sizeOption, isSoldOut: !variantEntry.isAvailable };
          })
        : sizeOptions;

    const sizeIdx = sizesForDefaultColor.findIndex(
      (option) => !option.isSoldOut
    );
    setSelectedSize(sizesForDefaultColor[sizeIdx >= 0 ? sizeIdx : 0].label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.stockId]);

  // Refine each size's isSoldOut to the specific selected-color+size combo
  // via variantMatrix (sizeOptions above is a coarse aggregate across all
  // colors), the same way CartEditModal does for the "select variant" modal.
  const selectedColorLabelForAvailability =
    colorOptions[selectedColorIdx]?.label;
  const sizeOptionsForSelectedColor = useMemo(() => {
    if (!selectedColorLabelForAvailability || !item?.variantMatrix) {
      return sizeOptions;
    }
    return sizeOptions.map((sizeOption) => {
      const key = `${selectedColorLabelForAvailability}::${sizeOption.label}`;
      const variantEntry = item.variantMatrix[key];
      if (typeof variantEntry?.isAvailable !== "boolean") {
        return sizeOption;
      }
      return { ...sizeOption, isSoldOut: !variantEntry.isAvailable };
    });
  }, [sizeOptions, selectedColorLabelForAvailability, item]);

  if (isLoading) {
    return null;
  }

  if (!item) {
    return (
      <div className={styles.missingWrap}>
        <button
          className={styles.backBtn}
          onClick={() => history.push({ pathname: backPath, state: backState })}
        >
          ⟵ Back to {backLabel}
        </button>
        <h2 className={styles.missingTitle}>Item not found</h2>
      </div>
    );
  }

  const hasColorOptions = colorOptions.length > 0;
  const hasSizeOptions = sizeOptions.length > 0;

  const selectedColorLabel = hasColorOptions
    ? colorOptions[selectedColorIdx]?.label || colorOptions[0]?.label || ""
    : null;
  const selectedImage = hasColorOptions
    ? colorOptions[selectedColorIdx]?.imageUrl || item.imageUrl
    : item.imageUrl;

  // Gallery follows the selected color's images (variant.images from
  // SingleStockGet). Falls back to the product-level productImages, then the
  // single imageUrl, else none.
  const colorImages = hasColorOptions
    ? colorOptions[selectedColorIdx]?.images
    : null;
  const galleryImages =
    Array.isArray(colorImages) && colorImages.length > 0
      ? colorImages.map((img) => (typeof img === "string" ? img : img.url))
      : Array.isArray(item.productImages) && item.productImages.length > 0
      ? item.productImages.map((img) =>
          typeof img === "string" ? img : img.url
        )
      : item.imageUrl
      ? [item.imageUrl]
      : [];

  const sizeLabel = hasSizeOptions
    ? selectedSize || item?.size || item?.defaultSize || sizeOptions[0]?.label
    : null;

  // Availability of the currently selected variant (not the product as a
  // whole - selecting a sold-out color/size should be allowed, it should
  // just hide the cart button and show the out-of-stock label).
  const selectedSizeOption = hasSizeOptions
    ? sizeOptionsForSelectedColor.find((option) => option.label === sizeLabel)
    : null;

  // Low-stock/out-of-stock status for the selected variant, computed the
  // same way the list view does it (via computeStockStatus/lowStockMessage)
  // using the specific color+size combo's quantityAvailable/lowStockThreshold
  // from variantMatrix - the product-level fields are meaningless for
  // CONFIGURABLE items, which only carry per-variant stock data.
  const selectedVariantMatrixEntry = item?.variantMatrix
    ? item.variantMatrix[`${selectedColorLabel || ""}::${sizeLabel || ""}`]
    : null;
  const selectedVariantStockStatus = computeStockStatus(
    selectedVariantMatrixEntry
      ? {
          isInStock: selectedVariantMatrixEntry.isInStock,
          quantityAvailable: selectedVariantMatrixEntry.quantityAvailable,
          lowStockThreshold: selectedVariantMatrixEntry.lowStockThreshold,
        }
      : item
  );
  const isSelectedVariantOutOfStock = hasSizeOptions
    ? Boolean(selectedSizeOption?.isSoldOut)
    : hasColorOptions
    ? Boolean(colorOptions[selectedColorIdx]?.isSoldOut)
    : selectedVariantStockStatus.isOutOfStock;
  const isSelectedVariantLowStock =
    !isSelectedVariantOutOfStock && selectedVariantStockStatus.isLowStock;

  const menuItems = [
    { label: "WWG Store", href: "/wwg-store" },
    { label: "My Lists", href: "/my-lists" },
    { label: "My Orders", href: "/my-orders" },
    { label: "Cart", href: "/cart" },
  ];

  const handleAddToCart = (updatedItem = {}) => {
    const rawProductId = item?.productId ?? item?.stockId ?? stockId;
    const parsedProductId = Number(rawProductId);
    const productId = Number.isNaN(parsedProductId)
      ? rawProductId
      : parsedProductId;
    const quantity = updatedItem?.quantity ?? qty;

    addToCart.mutate(
      {
        productId,
        variationId: updatedItem?.variationId ?? null,
        variantId: updatedItem?.variantId ?? null,
        quantity,
      },
      {
        onSuccess: () =>
          trackProductAdded(item, {
            quantity,
            color: updatedItem?.color ?? selectedColorLabel,
            size: updatedItem?.size ?? sizeLabel,
            cartId: cartData?.cartId ?? cartData?.id,
          }),
      }
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.desktopNav}>
        <Navbar
          onClick={() => history.goBack()}
          isHome={false}
          menuItems={menuItems}
          activeTab="WWG Store"
          onTabClick={(m) => history.push(m.href)}
          cartCount={cartCount}
        />
      </div>
      <div className={styles.content}>
        <div
          className={styles.backRow}
          onClick={() =>
            isFromList && listId
              ? history.push({ pathname: backPath, state: backState })
              : history.goBack()
          }
        >
          <div className={styles.backNav}>
            <img src={BackArrow} alt="Back" className={styles.backIcon} />
            <span className={styles.backLabel}>{backLabel}</span>
          </div>

          <div className={styles.badges}>
            {item.is3rdParty && (
              <img src={ThirdPartyIcon} alt="3rd party item" />
            )}
            {item.isHot && <img src={HotBadge} alt="Hot item" />}
            {item.isNew && <img src={NewBadge} alt="New item" />}
          </div>
        </div>
        <div className={styles.variantColumn}>
          <ProductGallery
            key={selectedColorLabel}
            images={galleryImages}
            title={item.title}
          />

          {hasColorOptions && (
            <>
              <div className={styles.galleryLabel}>Color</div>

              <div className={styles.thumbGrid}>
                {colorOptions.map((opt, i) => {
                  const selected = i === selectedColorIdx;
                  const isSoldOut = opt.isSoldOut;
                  return (
                    <button
                      key={`${opt.label}-${i}`}
                      type="button"
                      className={`${styles.thumb} ${
                        selected ? styles.thumbSelected : ""
                      } ${isSoldOut ? styles.thumbSoldOut : ""}`}
                      onClick={() => setSelectedColorIdx(i)}
                      aria-label={
                        isSoldOut ? `${opt.label} (Out of stock)` : opt.label
                      }
                    >
                      <div className={styles.thumbImg}>
                        <ItemImage
                          imageSrc={opt.imageUrl}
                          alt={opt.label}
                          isThumb
                        />
                      </div>
                      <div className={styles.thumbLabel}>{opt.label}</div>
                      {isSoldOut && (
                        <span
                          className={styles.thumbSoldOutMark}
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {hasSizeOptions && (
            <div className={styles.sizeSection}>
              <div className={styles.galleryLabel}>Size</div>
              <div className={styles.sizeGrid}>
                {sizeOptionsForSelectedColor.map((sizeOption) => {
                  const isSelected = selectedSize === sizeOption.label;
                  const isSoldOut = sizeOption.isSoldOut;

                  return (
                    <button
                      key={sizeOption.label}
                      type="button"
                      className={`${styles.sizePill} ${
                        isSelected ? styles.sizeSelected : ""
                      } ${isSoldOut ? styles.sizeSoldOut : ""}`}
                      onClick={() => setSelectedSize(sizeOption.label)}
                      aria-label={
                        isSoldOut
                          ? `${sizeOption.label} (Out of stock)`
                          : sizeOption.label
                      }
                      title={sizeOption.label}
                    >
                      <span className={styles.sizePillText}>
                        {getSizeAbbreviation(sizeOption.label)}
                      </span>
                      {isSoldOut && (
                        <span
                          className={styles.sizeSoldOutMark}
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className={styles.detailsCol}>
          <ItemDetails
            stockInfo={{
              ...item,
              color: selectedColorLabel,
              size: sizeLabel,
            }}
            detailsPage
            onQuantityChange={setQty}
            initialQty={qty}
            stockStatus={
              isSelectedVariantOutOfStock ? (
                <span className={styles.stockWarningLabel}>Out of Stock</span>
              ) : isSelectedVariantLowStock ? (
                <span className={styles.stockWarningLabel}>
                  {lowStockMessage(
                    selectedVariantStockStatus.quantityAvailable
                  )}
                </span>
              ) : null
            }
            actions={
              <div className={styles.iconsRow}>
                <ItemIconSection
                  item={{
                    stockId: item.stockId,
                    id: item.id,
                    title: item.title,
                    category: item.category,
                    author: item.author,
                    creator: item.creator,
                    isReadable: item.isReadable,
                    itemType: item.itemType,
                    memberPrice: item.memberPrice,
                    retailPrice: item.retailPrice,
                    quantity: qty,
                    image: selectedImage,
                    color: selectedColorLabel,
                    size: sizeLabel,
                    isLiked: item.isLiked,
                    isInList: item.isInList,
                    variants: item.variants,
                    detailColors: item.detailColors,
                    sizeOptions: item.sizeOptions,
                    variantMatrix: item.variantMatrix,
                    isOutOfStock: item.isOutOfStock,
                    isLowStock: item.isLowStock,
                    quantityAvailable: item.quantityAvailable,
                  }}
                  isItemDetails
                  hideNotify
                  hideOutOfStockLabel
                  hideLowStockLabel
                  disableCart={isSelectedVariantOutOfStock}
                  onCartClick={handleAddToCart}
                />
              </div>
            }
          />

          <div className={styles.descriptionBlock}>
            <h3 className={styles.subTitle}>Description</h3>
            <p className={styles.descriptionText}>
              {item.description || "No description available."}
            </p>
          </div>

          <div className={styles.noteBlock}>
            <div className={styles.noteHeader}>
              <span className={styles.noteTitle}>Personal Note</span>
              <img
                src={EditIcon}
                alt="Edit"
                className={styles.noteEditIcon}
                onClick={() => setIsEditingNote(true)}
                style={{ cursor: "pointer" }}
              />
            </div>
            {isEditingNote ? (
              <>
                <textarea
                  className={styles.noteTextarea}
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows={3}
                />
                <div className={styles.noteActions}>
                  {noteData?.text ? (
                    <Button
                      text="Delete"
                      className="deleteBtn"
                      onClick={() => {
                        updateNote.mutate({ stockId, text: "" });
                        setNoteText("");
                        setIsEditingNote(false);
                      }}
                    />
                  ) : (
                    <span />
                  )}
                  <div className={styles.noteActionsRight}>
                    <Button
                      text="Cancel"
                      className="cancelBtn"
                      onClick={() => {
                        setNoteText(noteData?.text ?? "");
                        setIsEditingNote(false);
                      }}
                    />
                    <Button
                      text="Save"
                      className="saveBtn"
                      onClick={() => {
                        updateNote.mutate({ stockId, text: noteText });
                        setIsEditingNote(false);
                      }}
                    />
                  </div>
                </div>
              </>
            ) : (
              <p className={styles.noteHint}>
                {noteText || "This is a personal note that only you can see"}
              </p>
            )}
          </div>

          <div className={styles.detailsSection}>
            <div className={styles.metaGrid}>
              <div>
                <span className={styles.metaKey}>Category:</span>{" "}
                {item.category || "-"}
              </div>
              <div>
                <span className={styles.metaKey}>Released:</span>{" "}
                {formatReleaseDate(item.releasedDate) || "-"}
              </div>
              <div>
                <span className={styles.metaKey}>SKU:</span> {item.sku || "-"}
              </div>
            </div>
          </div>
        </div>
      </div>

      <BottomNav cartCount={cartCount} activePath="/wwg-store" />
    </div>
  );
}
